import { useState } from "react";
import clsx from "clsx";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router";
import { UseMutateFunction } from "@tanstack/react-query";
import { z } from "zod";

import {
  JobStatusTransitionConfirmationDialog,
  RichTextEditorInput,
} from "@/components";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { useCurrentCompanyId } from "@/hooks";
import { useRefetchJobs } from "@/hooks/refetches";
import {
  compensationTypeOptions,
  employmentTypeOptions,
  jobStatusOptions as allJobStatusOptions,
  jobStatusOptionsForCreate,
  jobStatusOptionsForNonActiveUpdate,
  shiftTypeOptions,
  stateOptions,
} from "@/lib/constants";
import {
  CompensationType,
  CreateOrUpdateJobRequest,
  EmploymentType,
  Job,
  JobStatus,
  ShiftType,
} from "@/types";

const richTextEditorHeight = "260px";

const hourlyCompensationMax = 1000;
const yearlyCompensationMax = 1000000;

// TODO: Schema improvements:
// - Check enum error messages.
// - Check use of `min(1, ...)`.
// - Check coercion error messages.

const jobSchema = z
  .object({
    title: z.string().min(1, "Required"),
    address: z.string().min(1, "Required"),
    city: z.string().min(1, "Required"),
    state: z.string().min(1, "Required"),
    zipCode: z.string().min(1, "Required"),
    employmentType: z.nativeEnum(EmploymentType, { message: "Required" }),
    shiftType: z.nativeEnum(ShiftType, { message: "Required" }),
    shiftStart: z.string(),
    shiftEnd: z.string(),
    compensationType: z.nativeEnum(CompensationType, { message: "Required" }),
    compensationMin: z
      .union([z.string().min(1, "Required"), z.number()])
      .transform((val) => {
        if (typeof val === "string") return Number(val);
        return val;
      })
      .pipe(z.number().min(0, "Must be greater than 0")),
    compensationMax: z
      .union([z.string().min(1, "Required"), z.number()])
      .transform((val) => {
        if (typeof val === "string") return Number(val);
        return val;
      })
      .pipe(z.number().min(0, "Must be greater than 0")),
    benefits: z.string(),
    overview: z.string().min(1, "Required"),
    responsibilities: z.string().min(1, "Required"),
    qualifications: z.string().min(1, "Required"),
    status: z.nativeEnum(JobStatus, { message: "Required" }),
  })
  .superRefine((data, ctx) => {
    const maxLimit =
      data.compensationType === CompensationType.HOURLY
        ? hourlyCompensationMax
        : yearlyCompensationMax;
    const errorMessage =
      data.compensationType === CompensationType.HOURLY
        ? `Hourly compensation should not exceed $${hourlyCompensationMax.toLocaleString()}`
        : `Yearly compensation should not exceed $${yearlyCompensationMax.toLocaleString()}`;

    if (data.compensationMin >= maxLimit) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: errorMessage,
        path: ["compensationMin"],
      });
    }

    if (data.compensationMax >= maxLimit) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: errorMessage,
        path: ["compensationMax"],
      });
    }

    // Validate that maximum compensation is greater than or equal to minimum compensation if both exist.
    if (data.compensationMin >= data.compensationMax) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          "Maximum compensation must be greater than or equal to minimum compensation",
        path: ["compensationMax"],
      });
    }
  });

type JobSchema = z.infer<typeof jobSchema>;

const Divider = () => <div className="w-full border-t border-slate-200" />;

const Section = ({
  children,
  description,
  title,
}: {
  children: React.ReactElement | React.ReactElement[];
  description: string;
  title: string;
}) => {
  return (
    <div className="flex gap-20">
      <div className="flex w-[250px] flex-col gap-[2px]">
        <p className="text-lg font-semibold text-slate-700">{title}</p>
        <p className="text-sm font-medium text-slate-500">{description}</p>
      </div>
      <div className="flex w-full flex-col gap-6">{children}</div>
    </div>
  );
};

export const JobForm = ({
  className,
  job,
  mutationFn,
  submitButtonIcon,
  submitButtonText,
}: {
  className?: string;
  job?: Job;
  mutationFn: UseMutateFunction<
    Job,
    Error,
    {
      request: CreateOrUpdateJobRequest;
    }
  >;
  submitButtonIcon: React.ReactElement;
  submitButtonText: string;
}) => {
  const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] =
    useState(false);

  const form = useForm<JobSchema>({
    defaultValues: {
      title: job?.title ?? "",
      address: job?.address ?? "",
      city: job?.city ?? "",
      state: job?.state ?? "",
      zipCode: job?.zipCode ?? "",
      employmentType: job?.employmentType ?? EmploymentType.FULLTIME,
      shiftType: job?.shiftType ?? ShiftType.FIRSTSHIFT,
      shiftStart: job?.shiftStart ?? "",
      shiftEnd: job?.shiftEnd ?? "",
      compensationType: job?.compensationType ?? CompensationType.HOURLY,
      // Need to use `undefined` as the empty value so that zod interprets the value as missing if not specified.
      compensationMin: job?.compensationMin ?? undefined,
      // Need to use `undefined` as the empty value so that zod interprets the value as missing if not specified.
      compensationMax: job?.compensationMax ?? undefined,
      benefits: job?.benefits ?? "",
      overview: job?.overview ?? "",
      responsibilities: job?.responsibilities ?? "",
      qualifications: job?.qualifications ?? "",
      status: job?.status ?? JobStatus.DRAFT,
    },
    resolver: zodResolver(jobSchema),
  });
  const currentCompanyId = useCurrentCompanyId();
  const navigate = useNavigate();
  const refetchJobs = useRefetchJobs();

  const compensationPlaceholder =
    form.watch("compensationType") === CompensationType.HOURLY
      ? "$0 /hr"
      : "$0 /yr";

  const jobStatus = form.watch("status");

  // TODO: Improve this.
  // - If we are creating a job, we show: REVIEW, DRAFT.
  // - If we are updating a job that isn't active, we show: REVIEW, DRAFT, CLOSED.
  // - If we are updating a job that is active, we show: ACTIVE, REVIEW, DRAFT, CLOSED.
  const jobStatusOptions = !job
    ? jobStatusOptionsForCreate
    : job.status !== JobStatus.ACTIVE
      ? jobStatusOptionsForNonActiveUpdate
      : allJobStatusOptions;

  const handleSubmit = (data: JobSchema) => {
    if (
      // If creating a new job with REVIEW status, show confirmation dialog.
      (!job && data.status === JobStatus.REVIEW) ||
      // If editing a job and changing the status, show confirmation dialog.
      (job && job.status !== data.status) ||
      // If editing an `ACTIVE` job without changing the job status, show
      // the confirmation dialog since it will automatically place the job
      // in the `REVIEW` status.
      job?.status === JobStatus.ACTIVE
    ) {
      setIsConfirmationDialogOpen(true);
      return;
    }

    submitJob(data);
  };

  const submitJob = (data: JobSchema) => {
    mutationFn(
      {
        request: {
          ...data,
          // ! TODO: Find a more robust way to handle nullable fields.
          benefits: data.benefits || null,
          shiftStart: data.shiftStart || null,
          shiftEnd: data.shiftEnd || null,
        },
      },
      {
        onSuccess: () => {
          refetchJobs();
          // TODO: Find a way to navigate to the job details page on update.
          void navigate(`/companies/${currentCompanyId}/jobs`);
        },
      },
    );
  };

  return (
    <>
      <Form {...form}>
        <form
          className={clsx("flex flex-col gap-10", className)}
          // eslint-disable-next-line @typescript-eslint/no-misused-promises
          onSubmit={form.handleSubmit(handleSubmit)}
        >
          {/* Job details */}
          <Section
            title="1. Job Details"
            description="Provide details about the job."
          >
            {/* Job title */}
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem className="text-slate-700">
                  <FormLabel>Job title</FormLabel>
                  <FormControl>
                    <Input placeholder="Job title" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {/* Address */}
            <FormField
              control={form.control}
              name="address"
              render={({ field }) => (
                <FormItem className="text-slate-700">
                  <FormLabel>Address</FormLabel>
                  <FormControl>
                    <Input placeholder="Address" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="flex gap-4">
              {/* City */}
              <FormField
                control={form.control}
                name="city"
                render={({ field }) => (
                  <FormItem className="w-full text-slate-700">
                    <FormLabel>City</FormLabel>
                    <FormControl>
                      <Input placeholder="City" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              {/* State */}
              <FormField
                control={form.control}
                name="state"
                render={({ field }) => (
                  <FormItem className="w-full text-slate-700">
                    <FormLabel>State</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="State" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent className="text-slate-700">
                        {stateOptions.map((stateOption) => (
                          <SelectItem
                            key={stateOption.value}
                            value={stateOption.value}
                          >
                            {stateOption.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              {/* Zip code */}
              <FormField
                control={form.control}
                name="zipCode"
                render={({ field }) => (
                  <FormItem className="w-full text-slate-700">
                    <FormLabel>Zip code</FormLabel>
                    <FormControl>
                      <Input placeholder="Zip code" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </Section>

          {/* Divider */}
          <Divider />

          {/* Compensation */}
          <Section
            title="2. Compensation"
            description="Include pay details and benefit or perks your company offers."
          >
            <div className="flex gap-4">
              {/* Employment type */}
              <FormField
                control={form.control}
                name="employmentType"
                render={({ field }) => (
                  <FormItem className="w-full text-slate-700">
                    <FormLabel>Employment type</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Employment type" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent className="text-slate-700">
                        {employmentTypeOptions.map((employmentTypeOption) => (
                          <SelectItem
                            key={employmentTypeOption.value}
                            value={employmentTypeOption.value}
                          >
                            {employmentTypeOption.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              {/* Shift type */}
              <FormField
                control={form.control}
                name="shiftType"
                render={({ field }) => (
                  <FormItem className="w-full text-slate-700">
                    <FormLabel>Shift type</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Shift type" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent className="text-slate-700">
                        {shiftTypeOptions.map((shiftTypeOption) => (
                          <SelectItem
                            key={shiftTypeOption.value}
                            value={shiftTypeOption.value}
                          >
                            {shiftTypeOption.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="flex gap-4">
              {/* Shift start time */}
              <FormField
                control={form.control}
                name="shiftStart"
                render={({ field }) => (
                  <FormItem className="w-full text-slate-700">
                    <FormLabel>Shift start time (optional) </FormLabel>
                    <FormControl>
                      <Input type="time" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              {/* Shift end time */}
              <FormField
                control={form.control}
                name="shiftEnd"
                render={({ field }) => (
                  <FormItem className="w-full text-slate-700">
                    <FormLabel>Shift end time (optional)</FormLabel>
                    <FormControl>
                      <Input type="time" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="flex gap-4">
              {/* Compensation type */}
              <FormField
                control={form.control}
                name="compensationType"
                render={({ field }) => (
                  <FormItem className="w-full text-slate-700">
                    <FormLabel>Compensation type</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Compensation type" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent className="text-slate-700">
                        {compensationTypeOptions.map(
                          (compensationTypeOption) => (
                            <SelectItem
                              key={compensationTypeOption.value}
                              value={compensationTypeOption.value}
                            >
                              {compensationTypeOption.label}
                            </SelectItem>
                          ),
                        )}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              {/* Minimum */}
              <FormField
                control={form.control}
                name="compensationMin"
                render={({ field }) => (
                  <FormItem className="w-full text-slate-700">
                    <FormLabel>Minimum Compensation</FormLabel>
                    <FormControl>
                      <Input
                        placeholder={compensationPlaceholder}
                        type="number"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              {/* Maximum */}
              <FormField
                control={form.control}
                name="compensationMax"
                render={({ field }) => (
                  <FormItem className="w-full text-slate-700">
                    <FormLabel>Maximum Compensation</FormLabel>
                    <FormControl>
                      <Input
                        placeholder={compensationPlaceholder}
                        type="number"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            {/* Benefits */}
            <FormField
              control={form.control}
              name="benefits"
              render={({ field }) => (
                <FormItem className="text-slate-700">
                  <FormLabel>Benefits (optional)</FormLabel>
                  <FormControl>
                    <RichTextEditorInput
                      editorHeight={richTextEditorHeight}
                      onChange={field.onChange}
                      value={field.value}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </Section>

          {/* Divider */}
          <Divider />

          {/* Job description */}
          <Section
            title="3. Job Description"
            description="Include as much detail as possible about the role, responsibilities and required qualifications."
          >
            {/* Job summary */}
            <FormField
              control={form.control}
              name="overview"
              render={({ field }) => (
                <FormItem className="text-slate-700">
                  <FormLabel>Job summary</FormLabel>
                  <FormControl>
                    <RichTextEditorInput
                      editorHeight={richTextEditorHeight}
                      onChange={field.onChange}
                      value={field.value}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {/* Responsibilities */}
            <FormField
              control={form.control}
              name="responsibilities"
              render={({ field }) => (
                <FormItem className="text-slate-700">
                  <FormLabel>Responsibilities</FormLabel>
                  <FormControl>
                    <RichTextEditorInput
                      editorHeight={richTextEditorHeight}
                      onChange={field.onChange}
                      value={field.value}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {/* Qualifications */}
            <FormField
              control={form.control}
              name="qualifications"
              render={({ field }) => (
                <FormItem className="text-slate-700">
                  <FormLabel>Qualifications</FormLabel>
                  <FormControl>
                    <RichTextEditorInput
                      editorHeight={richTextEditorHeight}
                      onChange={field.onChange}
                      value={field.value}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </Section>

          {/* Divider */}
          <Divider />

          {/* Status */}
          <Section
            title="4. Job Status"
            description="Set the status of the job."
          >
            {/* Job Status */}
            <FormField
              control={form.control}
              name="status"
              render={({ field }) => (
                <FormItem className="text-slate-700">
                  <FormLabel>Job status</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Job status" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent className="text-slate-700">
                      {jobStatusOptions.map((jobStatusOption) => (
                        <SelectItem
                          key={jobStatusOption.value}
                          value={jobStatusOption.value}
                        >
                          {jobStatusOption.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          </Section>
          <div className="flex justify-end">
            <Button type="submit">
              {submitButtonIcon}
              {submitButtonText}
            </Button>
          </div>
        </form>
      </Form>
      <JobStatusTransitionConfirmationDialog
        jobStatus={
          // If editing an `ACTIVE` job without changing the job status, show the "to" status
          // as `REVIEW` since editing an active job will automatically place the job in the
          // `REVIEW` status.
          job?.status === JobStatus.ACTIVE && jobStatus === JobStatus.ACTIVE
            ? JobStatus.REVIEW
            : jobStatus
        }
        onOpenChange={setIsConfirmationDialogOpen}
        open={isConfirmationDialogOpen}
        onConfirm={() => {
          submitJob(form.getValues());
          setIsConfirmationDialogOpen(false);
        }}
      />
    </>
  );
};
